import * as _ from "lodash";
import { Injectable } from "@angular/core";
import { LgTranslateService } from "@logex/framework/lg-localization";
import * as Filters from "@logex/framework/lg-filterset";
import {
    FilterFactoryCreatorBase,
    IFilterFactoryEntry,
    MapFilterDefinition,
    MapFilterStore,
    SupportedFilters
} from "./bases/filter-factory-base";
import { AppDefinitions, DefinitionKey } from "./services";

// ---------------------------------------------------------------------------------------------
//  Implementation of the FilterFactory service
// ---------------------------------------------------------------------------------------------

@Injectable({
    providedIn: "root"
})
export class FilterFactory {
    constructor(
        private _filterSetService: Filters.LgFilterSetService,
        private _lgTranslateService: LgTranslateService,
        private _appDefinitions: AppDefinitions
    ) {
        // empty
    }

    define(): FilterFactoryCreator {
        return new FilterFactoryCreator(
            this._filterSetService,
            this._lgTranslateService,
            this._appDefinitions
        );
    }
}

// ---------------------------------------------------------------------------------------------
type Combo2Parameters<T> = Pick<
    Filters.IComboFilter2Definition<T>,
    | "source"
    | "onBecameActive"
    | "onBecameActiveLimit"
    | "onChanged"
    | "visible"
    | "name"
    | "nameLC"
>;

type CheckboxParameters = Pick<
    Filters.ICheckboxFilterDefinition,
    | "uncheckedFilters"
    | "default"
    | "previewTextLC"
    | "previewPopup"
    | "nameLC"
    | "onChanged"
    | "visible"
>;

type InputRangeParameters = Pick<
    Filters.IInputRangeFilterDefinition,
    "min" | "max" | "format" | "decimals" | "default" | "onChanged" | "visible" | "nameLC"
>;

// ---------------------------------------------------------------------------------------------
//  Implementation of the creator.
// ---------------------------------------------------------------------------------------------
export class FilterFactoryCreator<
    Definitions extends Record<string, Filters.INormalizedFilterDefinition> = {},
    Filters extends Filters.IFilterList = {}
> extends FilterFactoryCreatorBase {
    constructor(
        _filterSetService: Filters.LgFilterSetService,
        _lgTranslateService: LgTranslateService,
        private _appDefinitions: AppDefinitions
    ) {
        super(_filterSetService, _lgTranslateService);
    }

    // ---------------------------------------------------------------------------------------------
    addFilter<T extends SupportedFilters, N extends string>(
        id: N,
        params: T
    ): FilterFactoryCreator<
        Definitions & {
            [P in N]: MapFilterDefinition<T>;
        },
        Filters & { [P in N]: MapFilterStore<T> }
    > {
        return this._addFilter(id, params);
    }

    // ---------------------------------------------------------------------------------------------
    create(context: any): Filters.LgFilterSet<Definitions, Filters> {
        return this._create<Definitions, Filters>(context);
    }

    // ---------------------------------------------------------------------------------------------
    addCheckboxFilter<T extends string>(
        id: T,
        params: CheckboxParameters
    ): FilterFactoryCreator<any, any> {
        return this._createCheckbox(id, params, null);
    }

    // ---------------------------------------------------------------------------------------------
    addressTypeFilter<T extends string>(
        id: T,
        params: Combo2Parameters<string>
    ): FilterFactoryCreator<any, any> {
        return this._createCombo2String(id, params, {
            nameLC: "APP._Filters.AddressType",
            mapToOptions: this._createMapper<string>("addressType", true),
            allowNullValues: true
        });
    }

    // ---------------------------------------------------------------------------------------------
    contactTypeFilter<T extends string>(
        id: T,
        params: Combo2Parameters<string>
    ): FilterFactoryCreator<any, any> {
        return this._createCombo2String(id, params, {
            nameLC: "APP._Filters.ContactType",
            mapToOptions: this._createMapper<string>("contactType", true),
            allowNullValues: true
        });
    }

    // ---------------------------------------------------------------------------------------------
    countryFilter<T extends string>(
        id: T,
        params: Combo2Parameters<string>
    ): FilterFactoryCreator<any, any> {
        return this._createCombo2String(id, params, {
            nameLC: "APP._Filters.Country",
            mapToOptions: this._createMapper<string>("country", true),
            allowNullValues: true
        });
    }

    // ---------------------------------------------------------------------------------------------
    organizationTypeFilter<T extends string>(
        id: T,
        params: Combo2Parameters<string>
    ): FilterFactoryCreator<any, any> {
        return this._createCombo2String(id, params, {
            nameLC: "APP._Filters.OrganizationType",
            mapToOptions: this._createMapper<string>("organizationType", true),
            allowNullValues: true
        });
    }

    // ---------------------------------------------------------------------------------------------
    //  Private helpers
    // ---------------------------------------------------------------------------------------------

    private _createCombo2String<T extends string>(
        id: T,
        params: Combo2Parameters<string>,
        defaults: Partial<IFilterFactoryEntry<Filters.IComboFilter2Definition<string>>>
    ): FilterFactoryCreator<any, any> {
        return this.addFilter(id, {
            filterType: "combo2",
            main: true,
            idType: "string",
            visible: () => true,
            ...defaults,
            ...params
        } as IFilterFactoryEntry<Filters.IComboFilter2Definition<string>>);
    }

    // ---------------------------------------------------------------------------------------------
    private _createCheckbox<T extends string>(
        id: T,
        params: CheckboxParameters,
        defaults: Partial<IFilterFactoryEntry<Filters.ICheckboxFilterDefinition>>
    ): FilterFactoryCreator<any, any> {
        return this.addFilter(id, {
            filterType: "checkbox",
            main: true,
            visible: () => true,
            ...defaults,
            ...params
        } as IFilterFactoryEntry<Filters.ICheckboxFilterDefinition>);
    }

    // ---------------------------------------------------------------------------------------------
    private _createMapper<T extends number | string>(id: DefinitionKey, sortById: boolean) {
        return (codes: T[]) =>
            _(codes)
                .map(code => {
                    const definitionEntry = this._appDefinitions.getEntry(id, code);
                    const displayName = definitionEntry
                        ? definitionEntry.displayName
                        : this._lgTranslateService.translate("APP._Filters.Unknown");

                    return {
                        id: code,
                        order: code,
                        name: displayName
                    };
                })
                .sortBy(f => f.order)
                .value();
    }
}
